"use client";

/**
 * useChatbotFolders Hook
 *
 * Manages chatbot folder organization with tree structure support.
 * Follows the useFolders pattern for consistency.
 */
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import type { Chatbot, ChatbotFolder, ChatbotFolderWithChildren, CreateChatbotFolderInput, UpdateChatbotFolderInput } from "@/types/chatbot";
export type UseChatbotFoldersResult = {
  folders: ChatbotFolder[];
  folderTree: ChatbotFolderWithChildren[];
  defaultFolderId: string | null;
  loadingFolders: boolean;
  foldersError: string | null;
  createFolder: (input: CreateChatbotFolderInput) => Promise<ChatbotFolder | null>;
  updateFolder: (id: string, updates: UpdateChatbotFolderInput) => Promise<boolean>;
  deleteFolder: (id: string) => Promise<boolean>;
  moveFolder: (folderId: string, newParentId: string | null) => Promise<boolean>;
  moveChatbot: (chatbotId: string, folderId: string | null) => Promise<boolean>;
  reorderFolders: (folderId: string, newPosition: number, parentId: string | null) => Promise<boolean>;
  reorderChatbots: (chatbotId: string, newPosition: number, folderId: string | null) => Promise<boolean>;
  toggleFolderCollapse: (folderId: string) => Promise<boolean>;
  getOrCreateDefaultFolder: () => Promise<string | null>;
  refreshFolders: () => Promise<void>;
};

/**
 * Build a tree structure from flat folder list
 */
function buildChatbotFolderTree(folders: ChatbotFolder[], chatbots: Chatbot[]): ChatbotFolderWithChildren[] {
  // Create a map for quick lookup
  const folderMap = new Map<string, ChatbotFolderWithChildren>();

  // Initialize all folders with empty children and chatbots
  folders.forEach(folder => {
    folderMap.set(folder.id, {
      ...folder,
      children: [],
      chatbots: []
    });
  });

  // Assign chatbots to their folders
  chatbots.forEach(chatbot => {
    if (chatbot.folder_id && folderMap.has(chatbot.folder_id)) {
      folderMap.get(chatbot.folder_id)!.chatbots.push(chatbot);
    }
  });

  // Sort chatbots within each folder by position
  folderMap.forEach(folder => {
    folder.chatbots.sort((a, b) => a.position - b.position);
  });

  // Build tree structure
  const rootFolders: ChatbotFolderWithChildren[] = [];
  folders.forEach(folder => {
    const folderWithChildren = folderMap.get(folder.id)!;
    if (folder.parent_id && folderMap.has(folder.parent_id)) {
      // Add to parent's children
      folderMap.get(folder.parent_id)!.children.push(folderWithChildren);
    } else {
      // Add to root
      rootFolders.push(folderWithChildren);
    }
  });

  // Sort children within each folder by position
  const sortChildren = (folder: ChatbotFolderWithChildren) => {
    folder.children.sort((a, b) => a.position - b.position);
    folder.children.forEach(sortChildren);
  };
  rootFolders.sort((a, b) => a.position - b.position);
  rootFolders.forEach(sortChildren);
  return rootFolders;
}
type UseChatbotFoldersOptions = {
  onChatbotMoved?: () => void;
};
export function useChatbotFolders(userId: string | null | undefined, chatbots: Chatbot[], options: UseChatbotFoldersOptions = {}): UseChatbotFoldersResult {
  const {
    onChatbotMoved
  } = options;
  const [folders, setFolders] = useState<ChatbotFolder[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [foldersError, setFoldersError] = useState<string | null>(null);

  // Build tree structure whenever folders or chatbots change
  const folderTree = buildChatbotFolderTree(folders, chatbots);
  const refreshFolders = useCallback(async () => {
    if (!userId) return;
    setLoadingFolders(true);
    setFoldersError(null);
    try {
      const {
        data,
        error
      } = await supabase.from("chatbot_folders").select("*").eq("user_id", userId).order("position", {
        ascending: true
      });
      if (error) throw error;
      setFolders(data ?? []);
    } catch (err: any) {
      setFoldersError(err.message ?? "Failed to load chatbot folders");
    } finally {
      setLoadingFolders(false);
    }
  }, [userId]);
  useEffect(() => {
    if (!userId) {
      setFolders([]);
      return;
    }
    void refreshFolders();
  }, [userId, refreshFolders]);
  const createFolder = useCallback(async (input: CreateChatbotFolderInput): Promise<ChatbotFolder | null> => {
    if (!userId) return null;
    try {
      // Get the highest position among siblings to add at the end
      const siblings = folders.filter(f => f.parent_id === (input.parent_id || null));
      const maxPosition = siblings.length > 0 ? Math.max(...siblings.map(f_0 => f_0.position)) + 1 : 0;
      const {
        data: data_0,
        error: error_0
      } = await supabase.from("chatbot_folders").insert({
        user_id: userId,
        parent_id: input.parent_id || null,
        name: input.name,
        color: input.color || null,
        icon: input.icon || null,
        position: maxPosition
      }).select().single();
      if (error_0) throw error_0;
      if (!data_0) return null;
      setFolders(prev => [...prev, data_0]);
      return data_0;
    } catch (err_0: any) {
      console.error("[useChatbotFolders] Error creating folder:", err_0);
      setFoldersError(err_0.message ?? "Failed to create folder");
      return null;
    }
  }, [userId, folders]);
  const updateFolder = useCallback(async (id: string, updates: UpdateChatbotFolderInput): Promise<boolean> => {
    if (!userId) return false;
    try {
      const {
        error: error_1
      } = await supabase.from("chatbot_folders").update(updates).eq("id", id).eq("user_id", userId);
      if (error_1) throw error_1;
      setFolders(prev_0 => prev_0.map(f_1 => f_1.id === id ? {
        ...f_1,
        ...updates
      } as ChatbotFolder : f_1));
      return true;
    } catch (err_1: any) {
      console.error("[useChatbotFolders] Error updating folder:", err_1);
      setFoldersError(err_1.message ?? "Failed to update folder");
      return false;
    }
  }, [userId]);
  const deleteFolder = useCallback(async (id_0: string): Promise<boolean> => {
    if (!userId) return false;

    // Prevent deletion of default folder
    const folder = folders.find(f_2 => f_2.id === id_0);
    if (folder?.is_default) {
      setFoldersError("Cannot delete the default folder");
      return false;
    }
    try {
      const {
        error: error_2
      } = await supabase.from("chatbot_folders").delete().eq("id", id_0).eq("user_id", userId);
      if (error_2) throw error_2;

      // Remove folder and all its descendants from local state
      const getDescendantIds = (folderId: string): string[] => {
        const descendants: string[] = [];
        const children = folders.filter(f_3 => f_3.parent_id === folderId);
        children.forEach(child => {
          descendants.push(child.id);
          descendants.push(...getDescendantIds(child.id));
        });
        return descendants;
      };
      const idsToRemove = new Set([id_0, ...getDescendantIds(id_0)]);
      setFolders(prev_1 => prev_1.filter(f_4 => !idsToRemove.has(f_4.id)));
      return true;
    } catch (err_2: any) {
      console.error("[useChatbotFolders] Error deleting folder:", err_2);
      setFoldersError(err_2.message ?? "Failed to delete folder");
      return false;
    }
  }, [userId, folders]);
  const moveFolder = useCallback(async (folderId_0: string, newParentId: string | null): Promise<boolean> => {
    if (!userId) return false;
    try {
      // Get position for new parent's children
      const newSiblings = folders.filter(f_5 => f_5.parent_id === newParentId && f_5.id !== folderId_0);
      const maxPosition_0 = newSiblings.length > 0 ? Math.max(...newSiblings.map(f_6 => f_6.position)) + 1 : 0;
      const {
        error: error_3
      } = await supabase.from("chatbot_folders").update({
        parent_id: newParentId,
        position: maxPosition_0
      }).eq("id", folderId_0).eq("user_id", userId);
      if (error_3) throw error_3;
      setFolders(prev_2 => prev_2.map(f_7 => f_7.id === folderId_0 ? {
        ...f_7,
        parent_id: newParentId,
        position: maxPosition_0
      } : f_7));
      return true;
    } catch (err_3: any) {
      console.error("[useChatbotFolders] Error moving folder:", err_3);
      setFoldersError(err_3.message ?? "Failed to move folder");
      return false;
    }
  }, [userId, folders]);
  const moveChatbot = useCallback(async (chatbotId: string, folderId_1: string | null): Promise<boolean> => {
    if (!userId) return false;
    try {
      // Get position for the folder's chatbots
      const folderChatbots = chatbots.filter(c => c.folder_id === folderId_1 && c.id !== chatbotId);
      const maxPosition_1 = folderChatbots.length > 0 ? Math.max(...folderChatbots.map(c_0 => c_0.position)) + 1 : 0;
      const {
        error: error_4
      } = await supabase.from("chatbots").update({
        folder_id: folderId_1,
        position: maxPosition_1
      }).eq("id", chatbotId).eq("user_id", userId);
      if (error_4) throw error_4;

      // Refresh chatbots to update the UI
      if (onChatbotMoved) {
        onChatbotMoved();
      }
      return true;
    } catch (err_4: any) {
      console.error("[useChatbotFolders] Error moving chatbot:", err_4);
      setFoldersError(err_4.message ?? "Failed to move chatbot");
      return false;
    }
  }, [userId, chatbots, onChatbotMoved]);
  const reorderFolders = useCallback(async (folderId_2: string, newPosition: number, parentId: string | null): Promise<boolean> => {
    if (!userId) return false;
    try {
      // Get all siblings
      const siblings_0 = folders.filter(f_8 => f_8.parent_id === parentId && f_8.id !== folderId_2).sort((a, b) => a.position - b.position);

      // Insert at new position
      const folder_0 = folders.find(f_9 => f_9.id === folderId_2);
      if (!folder_0) return false;
      const reorderedSiblings = [...siblings_0];
      reorderedSiblings.splice(newPosition, 0, folder_0);

      // Update positions
      const updates_0 = reorderedSiblings.map((f_10, idx) => ({
        id: f_10.id,
        position: idx
      }));

      // Batch update
      await Promise.all(updates_0.map(u => supabase.from("chatbot_folders").update({
        position: u.position
      }).eq("id", u.id).eq("user_id", userId)));

      // Update local state
      setFolders(prev_3 => prev_3.map(f_11 => {
        const update = updates_0.find(u_0 => u_0.id === f_11.id);
        return update ? {
          ...f_11,
          position: update.position
        } : f_11;
      }));
      return true;
    } catch (err_5: any) {
      console.error("[useChatbotFolders] Error reordering folders:", err_5);
      setFoldersError(err_5.message ?? "Failed to reorder folders");
      return false;
    }
  }, [userId, folders]);
  const reorderChatbots = useCallback(async (chatbotId_0: string, newPosition_0: number, folderId_3: string | null): Promise<boolean> => {
    if (!userId) return false;
    try {
      // Get all chatbots in the folder
      const folderChatbots_0 = chatbots.filter(c_1 => c_1.folder_id === folderId_3 && c_1.id !== chatbotId_0).sort((a_0, b_0) => a_0.position - b_0.position);

      // Find the chatbot being moved
      const chatbot = chatbots.find(c_2 => c_2.id === chatbotId_0);
      if (!chatbot) return false;

      // Insert at new position
      const reorderedChatbots = [...folderChatbots_0];
      reorderedChatbots.splice(newPosition_0, 0, chatbot);

      // Update positions
      const updates_1 = reorderedChatbots.map((c_3, idx_0) => ({
        id: c_3.id,
        position: idx_0
      }));

      // Batch update
      await Promise.all(updates_1.map(u_1 => supabase.from("chatbots").update({
        position: u_1.position
      }).eq("id", u_1.id).eq("user_id", userId)));
      return true;
    } catch (err_6: any) {
      console.error("[useChatbotFolders] Error reordering chatbots:", err_6);
      setFoldersError(err_6.message ?? "Failed to reorder chatbots");
      return false;
    }
  }, [userId, chatbots]);
  const toggleFolderCollapse = useCallback(async (folderId_4: string): Promise<boolean> => {
    const folder_1 = folders.find(f_12 => f_12.id === folderId_4);
    if (!folder_1) return false;
    return updateFolder(folderId_4, {
      is_collapsed: !folder_1.is_collapsed
    });
  }, [folders, updateFolder]);
  const getOrCreateDefaultFolder = useCallback(async (): Promise<string | null> => {
    if (!userId) return null;
    try {
      // First check if we already have a default folder loaded
      const existingDefault = folders.find(f_13 => f_13.is_default);
      if (existingDefault) return existingDefault.id;

      // Check in database
      const {
        data: existing
      } = await supabase.from("chatbot_folders").select("id").eq("user_id", userId).eq("is_default", true).single();
      if (existing) return existing.id;

      // Create default folder if it doesn't exist
      const {
        data: newFolder,
        error: createError
      } = await supabase.from("chatbot_folders").insert({
        user_id: userId,
        name: "General",
        is_default: true,
        position: 0
      }).select().single();
      if (createError) {
        console.error("[useChatbotFolders] Error creating default folder:", createError);
        return null;
      }

      // Add to local state
      if (newFolder) {
        setFolders(prev_4 => [newFolder, ...prev_4]);
        return newFolder.id;
      }
      return null;
    } catch (err_7: any) {
      console.error("[useChatbotFolders] Error getting/creating default folder:", err_7);
      return null;
    }
  }, [userId, folders]);

  // Get the default folder ID from the loaded folders
  const defaultFolderId = folders.find(f_14 => f_14.is_default)?.id ?? null;
  return {
    folders,
    folderTree,
    defaultFolderId,
    loadingFolders,
    foldersError,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    moveChatbot,
    reorderFolders,
    reorderChatbots,
    toggleFolderCollapse,
    getOrCreateDefaultFolder,
    refreshFolders
  };
}